// SPDX-License-Identifier: Elastic-2.0
/**
 * useSidebarBadgeValue — maps a `sidebarConfig.badgeKey` to its
 * current reactive numeric value (or `undefined` when none requested).
 *
 * The hook ALWAYS subscribes to all backing queries (Rules of Hooks).
 * That's intentionally cheap: at MVP there's exactly one (pending
 * suggestions), and React Query de-duplicates subscribers across
 * components — the dashboard's HeroCard already mounts this query.
 *
 * Implementation note (vs. technical spec § 5.3):
 * The spec sketch assumed `usePendingSuggestionsCount` returns
 * `{ data: { count } }`, but the real hook (web/src/features/suggestions/
 * useSuggestions.ts) returns a plain `number` (with `0` standing in for
 * the "loading / empty" state). We pass that value straight through,
 * keeping the public surface (`number | undefined`) as written in the
 * spec so the consumer (CountBadge) sees a stable contract.
 */
import { usePendingSuggestionsCount } from "../suggestions/useSuggestions";
import type { SidebarBadgeKey } from "./sidebarConfig";

export function useSidebarBadgeValue(
  key: SidebarBadgeKey | undefined,
): number | undefined {
  const suggestionsCount = usePendingSuggestionsCount();
  if (key === undefined) return undefined;
  if (key === "suggestionsPending") return suggestionsCount;
  // Exhaustiveness — TS will fail compilation when SidebarBadgeKey grows.
  const _exhaustive: never = key;
  return _exhaustive;
}
